import { ApplicationTimeline } from '@services/ApplicationTimeline';
import { LatencyMetric } from './LatencyMetric';
import { Timeline } from '@src/services/Timeline';

/*
 * Classes to assist with measuring page latency in a framework-agnostic way.
 * To track page latency in your application, instantiate a PageLatencyTracker object for each page,
 * passing pageName and router listener function.
 * The router listener function should listen and fire on navigation.
 * Within each page of your application, access the assigned pageLatencyTracker, and send a checkpoint
 * from LATENCY_CP object (exported here) based on the lifecycle of that page.
 * An example of how this is done in IhmAssociateToolsBase app
 * can be seen [here](https://tiny.amazon.com/1hmzrazrm/codeamazpackIhmAblob8625src).
 * */

export const LATENCY_CP = Object.freeze({
    FirstByte: 'FirstByte',
    Render: 'Render',
    Click: 'Click',
    PageNav: 'PageNav',
});

interface ITimelineLatencyTracker {
    measureTimeBetween: (startCheckpoint: string, endCheckpoint: string, friendlyName?: string, operationName?: string) => (() => void) | void;
}

export class TimelineLatencyTracker implements ITimelineLatencyTracker {
    private readonly endCheckpointMetrics;
    private readonly startCheckpointMetrics;

    constructor() {
        this.endCheckpointMetrics = {};
        this.startCheckpointMetrics = {};
    }

    /**
     * @param startingCheckpoint: (String) (required) startingCheckPoint to measure latency between
     * @param endingCheckpoint: (String) (required) endingCheckpoint to measure latency between
     * @param friendlyName: (String) (optional) friendly name override for this metric
     * (default: startingCheckpoint + 'To' + endingCheckpoint)
     * @param operationName: (String) (optional) operationName for this latency metric
     * @output: (function) a function to call to deregister the callback handlers
     *
     * Examples:
     * CP1, CP2, CP3, CP4, CP3, CP4, CP5
     * MeasureTimeBetween(CP1, CP4) would record the duration between CP1 and the first CP4
     * MeasureTimeBetween(CP3, CP5) would record the duration between second CP3 and the CP5
     */
    public measureTimeBetween(startingCheckpoint: string, endingCheckpoint: string, friendlyName?: string, operationName?: string) {
        const metricName = friendlyName || `${startingCheckpoint}To${endingCheckpoint}`;
        const timeline = ApplicationTimeline.getTimeline();

        const latencyMetric = new LatencyMetric(undefined, metricName, 'IhmFlyingDutchman', operationName || 'Timeline');

        // Save metrics in instance variables so combinations of same start/end checkpoint can exist
        // Timeline does not support multiple or queued callbacks per event so must track here
        if (!this.startCheckpointMetrics[startingCheckpoint]) {
            this.startCheckpointMetrics[startingCheckpoint] = [latencyMetric];
        } else {
            this.startCheckpointMetrics[startingCheckpoint].push(latencyMetric);
        }

        if (!this.endCheckpointMetrics[endingCheckpoint]) {
            this.endCheckpointMetrics[endingCheckpoint] = [latencyMetric];
        } else {
            this.endCheckpointMetrics[endingCheckpoint].push(latencyMetric);
        }

        const startCheckpointListenerDeReg = timeline.onCheckpoint(
            startingCheckpoint,
            (() => {
                const metricsToStart = this.startCheckpointMetrics[startingCheckpoint] || [];
                metricsToStart.forEach((metric: LatencyMetric) => metric.start());
            }).bind(this),
        );

        const endCheckpointListenerDeReg = timeline.onCheckpoint(
            endingCheckpoint,
            (() => {
                const metrics = this.endCheckpointMetrics[endingCheckpoint] || [];
                metrics.forEach((metric: LatencyMetric) => {
                    if (metric.getIsStarted()) {
                        metric.stop();
                    }
                });
            }).bind(this),
        );

        // Return deRegistration function
        return function () {
            startCheckpointListenerDeReg();
            endCheckpointListenerDeReg();
        };
    }
}

type RouteChangeListenerFunction = (callback: () => void) => () => void;

export class PageLatencyTracker extends TimelineLatencyTracker {
    private trackedDeRegistrationFunctions: Array<() => void>;
    private readonly markedCheckpoints: { [checkpoint: string]: boolean };
    private readonly pageName: string;
    private timeline: Timeline;
    private readonly routerListener: RouteChangeListenerFunction;
    private routeChangeStartDestroyFunction: () => void;
    public private;

    constructor(pageName: string, routerListener: RouteChangeListenerFunction) {
        super();
        this.trackedDeRegistrationFunctions = [];
        this.markedCheckpoints = {};
        this.pageName = pageName;
        this.timeline = ApplicationTimeline.getTimeline();
        this.routerListener = routerListener;
        this.init();
    }

    /**
     * @param checkpoint1: (String) (required) startingCheckPoint to measure latency between
     * @param checkpoint2: (String) (required) endingCheckpoint to measure latency between
     * @param friendlyName: (String) (optional) friendly name override for
     * this metric (default: startingCheckpoint + 'To' + endingCheckpoint)
     * @sample: measureTimeBetween(FIRST_BYTE_CP, "AllImagesDownloaded");
     * @note: This function needs to be called before the corresponding checkpoint()
     * @note: This function appends a prefix to the checkpoint name. Format: pageName + '.' + checkpointName
     * @note: This function has no return because deRegistration is already handled
     */
    public measureTimeBetween(checkpoint1: string, checkpoint2: string, friendlyName?: string, operationName?: string) {
        // Validate that measureTimeBetween is called before checkpoint
        if (checkpoint1 in this.markedCheckpoints || checkpoint2 in this.markedCheckpoints) {
            throw new Error('measureTimeBetween() must be called before checkpoint().');
        }

        const metricName = friendlyName || `${checkpoint1}To${checkpoint2}`;
        this.trackDeRegistrationFunction(super.measureTimeBetween(this.addPrefix(checkpoint1), this.addPrefix(checkpoint2), metricName, this.pageName));
        // Added this to match the return type of measureTimeBetween function in the base class.
        return function () {
            return;
        };
    }

    /**
     * @param checkpoint: (String) (required) checkpoint to mark
     * @sample: checkpoint("AllImagesDownloaded");
     * @note: This function needs to be called after any corresponding measureTimeBetween()
     * @note: This function appends a prefix to the checkpoint name. Format: pageName + '.' + checkpointName
     */
    public checkpoint(checkpoint: string) {
        this.timeline.checkpoint(this.addPrefix(checkpoint));
        this.markedCheckpoints[checkpoint] = true;
    }

    private addPrefix(checkpoint: string) {
        return `${this.pageName}.${checkpoint}`;
    }

    private trackDeRegistrationFunction(deRegistrationFunction: () => void) {
        this.trackedDeRegistrationFunctions = this.trackedDeRegistrationFunctions.concat(deRegistrationFunction);
    }

    private init() {
        this.measureTimeBetween(LATENCY_CP.FirstByte, LATENCY_CP.Render);
        this.measureTimeBetween(LATENCY_CP.Click, LATENCY_CP.PageNav);
        this.measureTimeBetween(LATENCY_CP.FirstByte, LATENCY_CP.PageNav);

        // Binding this so that listener can unsubscribe itself on route change
        this.routeChangeStartDestroyFunction = this.routerListener(
            (() => {
                for (let i = 0; i < this.trackedDeRegistrationFunctions.length; i++) {
                    this.trackedDeRegistrationFunctions[i]();
                }
                try {
                    this.routeChangeStartDestroyFunction();
                } catch (e) {
                    console.error('Error when trying to unsubscribe using passed route listener: ' + e);
                }
            }).bind(this),
        );
    }
}
